import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtTime } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Bike, Clock, AlertTriangle } from "lucide-react";

function formatSla(sec) {
  if (sec == null) return "—";
  const abs = Math.abs(sec);
  const m = Math.floor(abs / 60);
  const s = abs % 60;
  const pad = `${m}:${String(s).padStart(2, "0")}`;
  return sec < 0 ? `-${pad}` : pad;
}

export default function AggregatorBoard() {
  const qc = useQueryClient();
  const [brandFilter, setBrandFilter] = useState("");

  const { data: brands = [] } = useQuery({
    queryKey: ["brands"],
    queryFn: async () => (await api.get("/brands")).data,
  });

  const { data: orders = [], isFetching } = useQuery({
    queryKey: ["aggregator-board", brandFilter],
    queryFn: async () => {
      const q = brandFilter ? `?brand_id=${encodeURIComponent(brandFilter)}` : "";
      return (await api.get(`/aggregator/board${q}`)).data;
    },
    refetchInterval: 5000,
  });

  const setStatus = async (o, action) => {
    try {
      await api.post(`/orders/${o.id}/kitchen-status`, { action });
      const externalId = o.external_order_id || o.external_id;
      if (o.channel && externalId && ["zomato", "swiggy"].includes(String(o.channel))) {
        const map = { cooking: "preparing", ready: "ready", handed: "picked_up" };
        try {
          await api.post(`/integrations/channels/${o.channel}/orders/${externalId}/status`, {
            status: map[action] || action,
          });
        } catch { /* channel push best-effort */ }
      }
      toast.success(`${o.order_number} → ${action}`);
      qc.invalidateQueries({ queryKey: ["aggregator-board"] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    }
  };

  const sorted = useMemo(
    () => [...orders].sort((a, b) => (a.sla_remaining_sec ?? 0) - (b.sla_remaining_sec ?? 0)),
    [orders],
  );

  return (
    <div className="h-full overflow-y-auto p-6 lg:p-8" data-testid="aggregator-board">
      <PageHeader title="Aggregator Board" subtitle="Live delivery & takeaway tickets · refreshes every 5s">
        <div className="flex items-center gap-2">
          <select
            value={brandFilter}
            onChange={(e) => setBrandFilter(e.target.value)}
            className="h-9 rounded-lg border px-2 text-sm"
          >
            <option value="">All brands</option>
            {brands.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
          {isFetching && <span className="text-xs text-neutral-400">Updating…</span>}
        </div>
      </PageHeader>

      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3 max-w-6xl">
        {sorted.map((o) => {
          const overdue = !!o.sla_overdue || (o.sla_remaining_sec != null && o.sla_remaining_sec < 0);
          const urgent = !overdue && o.sla_remaining_sec != null && o.sla_remaining_sec < 300;
          return (
            <div
              key={o.id}
              className={`kul-card p-4 border-l-4 ${
                overdue ? "border-l-red-500 bg-red-50/40" : urgent ? "border-l-amber-400" : "border-l-[var(--kul-primary)]"
              }`}
              data-testid={`agg-order-${o.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold truncate">{o.order_number}</div>
                  <div className="text-xs text-neutral-500 capitalize flex items-center gap-1 mt-0.5">
                    <Bike className="w-3 h-3" />
                    {o.channel || o.order_type}
                    {o.brand_name ? ` · ${o.brand_name}` : ""}
                  </div>
                </div>
                <div className={`flex items-center gap-1 font-mono-k text-sm font-bold ${overdue ? "text-red-600" : urgent ? "text-amber-600" : "text-neutral-700"}`}>
                  {overdue ? <AlertTriangle className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
                  {formatSla(o.sla_remaining_sec)}
                </div>
              </div>
              <div className="mt-2 text-sm space-y-0.5">
                {(o.items || []).slice(0, 4).map((it, i) => (
                  <div key={i} className="truncate">{it.qty}× {it.name}</div>
                ))}
                {(o.items || []).length > 4 && (
                  <div className="text-xs text-neutral-400">+{(o.items || []).length - 4} more</div>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-1.5 mt-3">
                <Badge className="capitalize bg-neutral-100 text-neutral-600">{o.status}</Badge>
                {overdue && <Badge className="bg-red-100 text-red-700">Overdue</Badge>}
                {o.customer_name && <span className="text-[11px] text-neutral-400 truncate">{o.customer_name}</span>}
                <span className="text-[11px] text-neutral-400 ml-auto">{fmtTime(o.created_at)}</span>
              </div>
              <div className="flex gap-1 mt-3 flex-wrap">
                {o.status !== "preparing" && o.status !== "ready" && o.status !== "completed" && (
                  <Button size="sm" variant="outline" onClick={() => setStatus(o, "cooking")} data-testid={`agg-cook-${o.id}`}>Cook</Button>
                )}
                {o.status !== "ready" && o.status !== "completed" && (
                  <Button size="sm" variant="outline" onClick={() => setStatus(o, "ready")} data-testid={`agg-ready-${o.id}`}>Ready</Button>
                )}
                {o.status !== "completed" && (
                  <Button size="sm" onClick={() => setStatus(o, "handed")} className="bg-[var(--kul-primary)] text-white" data-testid={`agg-hand-${o.id}`}>Handed</Button>
                )}
              </div>
            </div>
          );
        })}
        {!sorted.length && (
          <p className="text-neutral-400 col-span-full py-12 text-center">No open aggregator / delivery tickets.</p>
        )}
      </div>
    </div>
  );
}
